import { Controller } from "@hotwired/stimulus"
import { creditsForPages, smallestPackCovering } from "../lib/pdf_pricing"
import { interpolate } from "../lib/interpolate"

// Orchestrates the upload form: tab blocking when content exists,
// whole-form drag-drop, and submit button state management
export default class extends Controller {
  static targets = ["submitButton", "submitLabel", "textTitle", "textBody", "urlInput", "fileInput", "tabsContainer", "fileDropContainer", "form", "sendIcon", "pdfPricing", "pdfPricingText", "pdfPricingExplanation", "pdfPricingAllPacks", "pdfSendOriginal", "pdfAuthorizedCredits", "skipPdfConversion", "uploadType"]
  static outlets = ["file-drop", "tabs"]
  static values = {
    creditBalance: Number,
    creditPacks: { type: Array, default: [] },
    // No JS-side copy of the pricing numbers: the view wires these from
    // PdfConversionCredits::PAGES_PER_CREDIT and PdfPageCounter::MAX_PAGES.
    pagesPerCredit: Number,
    maxPdfPages: Number,
    maxDeliverableSize: { type: Number, default: 0 },
    pdfConversionEnabled: { type: Boolean, default: true }, // false when the user prefers PDFs as they are
    pdfReaderUrl: String,
    pdfWorkerUrl: String,
    i18n: { type: Object, default: {} }
  }

  // Tab indices (must match template order: Link, File, Text)
  static TAB_LINK = 0
  static TAB_FILE = 1
  static TAB_TEXT = 2

  connect() {
    this.hasFile = false
    this.defaultSubmitLabel = this.hasSubmitLabelTarget ? this.submitLabelTarget.textContent : ""
    this.updateSubmitButton()

    // Listen for turbo submit end (to stop animation on error)
    this.boundOnSubmitEnd = this.onSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this.boundOnSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd)
    this.pdfReadGeneration = (this.pdfReadGeneration || 0) + 1
  }

  // Start send animation when form submits
  startSendAnimation() {
    if (!this.hasSendIconTarget) return

    this.sendIconTarget.classList.add("sending")

    // When animation ends, clean up and update button state
    const device = this.sendIconTarget.querySelector(".send-kindle-device")
    if (device) {
      device.addEventListener("animationend", () => {
        this.sendIconTarget.classList.remove("sending")
        // Form fields have been reset via turbo_stream, update button state
        this.hasFile = false
        this.updateSubmitButton()
      }, { once: true })
    }
  }

  // Handle turbo submit end
  onSubmitEnd(event) {
    if (!event.detail.success) {
      // On error, stop animation immediately
      this.stopSendAnimation()
      return
    }
    // On success, the turbo_stream resets the form fields, but the price banner
    // and the submit label sit outside them. The animation runs to its end.
    this.hasFile = false
    this.clearPdfPricing()
    this.updateSubmitButton()
  }

  // Stop send animation (called on error)
  stopSendAnimation() {
    if (this.hasSendIconTarget) {
      this.sendIconTarget.classList.remove("sending")
    }
  }

  // Event handler for tabs:before-switch - block if current tab has content
  onBeforeTabSwitch(event) {
    const { fromIndex } = event.detail

    if (this.hasContentInTab(fromIndex)) {
      event.preventDefault()

      // Show tooltip and animate the blocking field
      this.showBlockedFeedback(fromIndex, event.detail.toIndex)
    }
  }

  // Keep the hidden upload_type field in sync with the active tab.
  // Handles tabs:switched (user clicks) and tabs:restored (initial page
  // load and the turbo_stream form-fields replacement after a successful
  // submit, where the tabs controller restores the saved tab without
  // dispatching tabs:switched). An outlet-connected callback is NOT
  // reliable here: it can fire before the tabs controller's connect()
  // restores the saved index, reading the stale default and leaving
  // upload_type as "link" while the File tab is visually active.
  onTabSwitched(event) {
    this.syncUploadType(event.detail.index)
  }

  syncUploadType(index) {
    if (!this.hasUploadTypeTarget) return

    const types = {
      [this.constructor.TAB_LINK]: "link",
      [this.constructor.TAB_FILE]: "file",
      [this.constructor.TAB_TEXT]: "text"
    }
    this.uploadTypeTarget.value = types[index] ?? ""
  }

  // Check if a specific tab has content that would block switching
  hasContentInTab(tabIndex) {
    switch (tabIndex) {
      case this.constructor.TAB_LINK:
        return this.hasUrlContent()
      case this.constructor.TAB_FILE:
        return this.hasFile
      case this.constructor.TAB_TEXT:
        return this.hasTextContent()
      default:
        return false
    }
  }

  // Show feedback when tab switch is blocked
  showBlockedFeedback(fromIndex, toIndex) {
    // Show tooltip on tabs controller
    if (this.hasTabsOutlet) {
      this.tabsOutlet.showTooltip(this.translation("clear_field_first"), toIndex)
    }

    // Animate the blocking field with ring pulse
    this.animateBlockingField(fromIndex)
  }

  // Apply ring pulse animation to the field blocking the switch
  animateBlockingField(tabIndex) {
    let fields = []

    switch (tabIndex) {
      case this.constructor.TAB_LINK:
        if (this.hasUrlInputTarget) fields.push(this.urlInputTarget)
        break
      case this.constructor.TAB_FILE:
        if (this.hasFileDropContainerTarget) {
          const dropZone = this.fileDropContainerTarget.querySelector("[data-file-drop-target='dropZone']")
          if (dropZone) fields.push(dropZone)
        }
        break
      case this.constructor.TAB_TEXT:
        // Highlight whichever field(s) have content
        if (this.hasTextTitleTarget && this.textTitleTarget.value.trim().length > 0) {
          fields.push(this.textTitleTarget)
        }
        if (this.hasTextBodyTarget && this.textBodyTarget.value.trim().length > 0) {
          fields.push(this.textBodyTarget)
        }
        break
    }

    fields.forEach(field => {
      field.classList.add("animate-ring-pulse")
      setTimeout(() => {
        field.classList.remove("animate-ring-pulse")
      }, 500)
    })
  }

  // Whole-form drag handlers
  formDragover(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasFormTarget) return

    // Check if we can accept a file drop
    const currentTabIndex = this.getCurrentTabIndex()
    const canAccept = currentTabIndex === this.constructor.TAB_FILE ||
                      (!this.hasContentInTab(this.constructor.TAB_LINK) &&
                       !this.hasContentInTab(this.constructor.TAB_TEXT))

    if (canAccept) {
      this.formTarget.classList.add("drag-over-allowed")
      this.formTarget.classList.remove("drag-over-blocked")
    } else {
      this.formTarget.classList.add("drag-over-blocked")
      this.formTarget.classList.remove("drag-over-allowed")
    }
  }

  formDragleave(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasFormTarget) return

    // Only remove classes if we're leaving the form entirely
    const relatedTarget = event.relatedTarget
    if (!this.formTarget.contains(relatedTarget)) {
      this.formTarget.classList.remove("drag-over-allowed", "drag-over-blocked")
    }
  }

  formDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasFormTarget) return

    this.formTarget.classList.remove("drag-over-allowed", "drag-over-blocked")

    const files = event.dataTransfer.files
    if (files.length === 0) return

    const currentTabIndex = this.getCurrentTabIndex()

    // If we're already on file tab, let file-drop handle it
    if (currentTabIndex === this.constructor.TAB_FILE) {
      // Delegate to file-drop controller
      if (this.hasFileDropOutlet) {
        this.fileDropOutlet.acceptDroppedFiles(files)
      }
      return
    }

    // Check if we can switch to file tab
    if (this.hasContentInTab(this.constructor.TAB_LINK) || this.hasContentInTab(this.constructor.TAB_TEXT)) {
      // Block the drop - show red flash
      this.showBlockedDropFeedback()
      return
    }

    // Auto-switch to file tab and accept the file
    if (this.hasTabsOutlet) {
      this.tabsOutlet.switchToTab(this.constructor.TAB_FILE)
    }

    // Give the DOM a moment to update, then handle the file
    setTimeout(() => {
      if (this.hasFileDropOutlet) {
        this.fileDropOutlet.acceptDroppedFiles(files)
      }
    }, 50)
  }

  // Show blocked drop feedback (red flash on drop zone)
  showBlockedDropFeedback() {
    if (!this.hasFileDropContainerTarget) return

    const dropZone = this.fileDropContainerTarget.querySelector("[data-file-drop-target='dropZone']")
    if (!dropZone) return

    dropZone.classList.add("drop-blocked-flash")
    setTimeout(() => {
      dropZone.classList.remove("drop-blocked-flash")
    }, 500)
  }

  getCurrentTabIndex() {
    if (this.hasTabsOutlet) {
      return this.tabsOutlet.getCurrentIndex()
    }
    return 0
  }

  // File input change handler - forwards to visible file-drop outlet
  selectFile(event) {
    const files = event.target.files
    if (files.length === 0) return

    // Forward to file-drop controller
    if (this.hasFileDropOutlet) {
      this.fileDropOutlet.handleFileFromInput(files[0])
    }
  }

  // Event handler for file-drop:selected
  fileSelected(event) {
    this.hasFile = true
    this.updateSubmitButton()
    this.pricePdf(event.detail.file)
  }

  // Event handler for file-drop:removed
  fileRemoved(event) {
    this.hasFile = false
    this.clearPdfPricing()
    this.updateSubmitButton()
  }

  // URL input handler
  onUrlInput(event) {
    this.updateSubmitButton()
  }

  // Text input handler
  onTextInput(event) {
    this.updateSubmitButton()
  }

  // Check if URL field has content (any non-empty text)
  hasUrlContent() {
    if (!this.hasUrlInputTarget) return false
    return this.urlInputTarget.value.trim().length > 0
  }

  // Check if any URL input has a valid URL
  hasValidUrl() {
    if (!this.hasUrlInputTarget) return false

    const url = this.urlInputTarget.value.trim()
    if (!url) return false

    try {
      const parsed = new URL(url)
      return ['http:', 'https:'].includes(parsed.protocol)
    } catch {
      return false
    }
  }

  // Check if text tab has content (title or body)
  hasTextContent() {
    const hasTitle = this.hasTextTitleTarget && this.textTitleTarget.value.trim().length > 0
    const hasBody = this.hasTextBodyTarget && this.textBodyTarget.value.trim().length > 0
    return hasTitle || hasBody
  }

  // Submit button state
  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const hasText = this.hasTextContent()
    const hasUrl = this.hasValidUrl()
    const hasContent = hasText || this.hasFile || hasUrl

    if (hasContent) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.submitButtonTarget.classList.add("cursor-pointer")
    } else {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitButtonTarget.classList.remove("cursor-pointer")
    }
  }

  async pricePdf(file) {
    this.clearPdfPricing()
    if (!this.pdfConversionEnabledValue || !file || !this.isPdf(file)) return

    const generation = (this.pdfReadGeneration || 0) + 1
    this.pdfReadGeneration = generation
    this.showPdfPricing(this.translation("reading_pdf"))
    this.submitButtonTarget.disabled = true

    try {
      const reader = await import(this.pdfReaderUrlValue)
      const pages = await Promise.race([
        reader.pdfPageCount(file, this.pdfWorkerUrlValue),
        new Promise((_, reject) => setTimeout(() => reject(new Error("PDF read timeout")), 8000))
      ])
      if (generation !== this.pdfReadGeneration) return

      if (pages > this.maxPdfPagesValue) {
        this.showPdfPricing(this.translation("too_many_pages", { pages, limit: this.maxPdfPagesValue }), true)
        this.submitButtonTarget.disabled = true
        // Nothing this long can be converted at all, so the file as it is is the
        // only way it reaches a Kindle.
        this.revealOriginalOffer(file)
        return
      }

      const credits = creditsForPages(pages, this.pagesPerCreditValue)
      this.pdfAuthorizedCreditsTarget.value = credits
      const creditLabel = this.creditLabel(credits)
      if (this.creditBalanceValue < credits) {
        const deficit = credits - this.creditBalanceValue
        // No packs means no checkout on this client — an app too old to sell
        // credits. The link beside the price explains that; the submit label
        // stays neutral rather than promising a conversion we cannot start.
        const pack = smallestPackCovering(this.creditPacksValue, deficit)

        this.showPdfPricing(
          [
            this.translation("summary", { pages, credits: creditLabel }),
            this.translation("balance", { credits: this.creditLabel(this.creditBalanceValue) }),
            this.translation("needs_more", { credits: this.creditLabel(deficit) })
          ].join("\n")
        )
        if (pack) {
          const key = pack.price ? "add_credits" : "add_credits_without_price"
          this.submitLabelTarget.textContent = this.translation(key, { credits: this.creditLabel(pack.credits), price: pack.price })
        }
        if (this.hasPdfPricingAllPacksTarget) this.pdfPricingAllPacksTarget.classList.remove("hidden")
        this.trackPaidPdf("paid_pdf_price_shown", { pages, credits, sufficient_balance: false })
      } else {
        this.showPdfPricing(this.translation("summary", { pages, credits: creditLabel }))
        this.submitLabelTarget.textContent = this.translation("convert_and_send", { credits: creditLabel })
        this.trackPaidPdf("paid_pdf_price_shown", { pages, credits, sufficient_balance: this.creditBalanceValue >= credits })
      }
      this.revealOriginalOffer(file)
      this.updateSubmitButton()
    } catch (_error) {
      if (generation !== this.pdfReadGeneration) return
      this.pdfAuthorizedCreditsTarget.value = ""
      this.showPdfPricing(this.translation("unavailable"))
      this.updateSubmitButton()
    }
  }

  clearPdfPricing() {
    this.pdfReadGeneration = (this.pdfReadGeneration || 0) + 1
    if (this.hasPdfAuthorizedCreditsTarget) this.pdfAuthorizedCreditsTarget.value = ""
    if (this.hasSkipPdfConversionTarget) this.skipPdfConversionTarget.value = ""
    if (this.hasPdfPricingTarget) this.pdfPricingTarget.classList.add("hidden")
    if (this.hasPdfPricingAllPacksTarget) this.pdfPricingAllPacksTarget.classList.add("hidden")
    if (this.hasPdfPricingExplanationTarget) this.pdfPricingExplanationTarget.classList.add("hidden")
    if (this.hasPdfSendOriginalTarget) this.pdfSendOriginalTarget.classList.add("hidden")
    if (this.hasSubmitLabelTarget) this.submitLabelTarget.textContent = this.defaultSubmitLabel
  }

  // The alternative to paying is mailing the file itself, which only exists
  // while the file fits in an email. Offering it past that limit would promise
  // a delivery that bounces.
  revealOriginalOffer(file) {
    if (this.hasPdfPricingExplanationTarget) this.pdfPricingExplanationTarget.classList.remove("hidden")
    if (!this.hasPdfSendOriginalTarget) return
    if (this.maxDeliverableSizeValue > 0 && file.size > this.maxDeliverableSizeValue) return

    this.pdfSendOriginalTarget.classList.remove("hidden")
  }

  sendOriginal() {
    if (!this.hasFormTarget) return

    if (this.hasSkipPdfConversionTarget) this.skipPdfConversionTarget.value = "1"
    if (this.hasPdfAuthorizedCreditsTarget) this.pdfAuthorizedCreditsTarget.value = ""
    this.trackPaidPdf("paid_pdf_original_chosen")
    this.submitButtonTarget.disabled = false
    this.formTarget.requestSubmit()
  }

  showPdfPricing(message, error = false) {
    if (!this.hasPdfPricingTarget || !this.hasPdfPricingTextTarget) return
    this.pdfPricingTarget.classList.remove("hidden")
    this.pdfPricingTextTarget.textContent = message
    this.pdfPricingTextTarget.classList.toggle("text-error", error)
    this.pdfPricingTextTarget.classList.toggle("text-ink-secondary", !error)
  }

  creditLabel(count) {
    const forms = this.i18nValue.credit_units || {}
    const locale = this.i18nValue.locale || document.documentElement.lang || "en"
    const category = new Intl.PluralRules(locale).select(count)
    const unit = forms[category] || forms.other || forms.one || ""
    return `${count} ${unit}`.trim()
  }

  translation(key, values = {}) {
    return interpolate(this.i18nValue[key], values)
  }

  isPdf(file) {
    return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  }

  trackPaidPdf(event, attributes = {}) {
    if (typeof window.gtag !== "function") return
    window.gtag("event", event, attributes)
  }
}
